"use client";

import React, { useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Plus, ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
interface FaqItem {
  id: string;
  code: string;
  question: string;
  answer: string;
  relatedLinkText: string;
  relatedLinkTo: string;
}
const faqData: FaqItem[] = [{
  id: "bull-cost",
  code: "COST-01",
  question: "How much does a mechanical bull cost?",
  answer: "Mechanical bull prices vary according to the machine, condition, controller, included equipment, inflatable safety area, accessories and delivery requirements. Compare current products rather than relying on one generic industry price.",
  relatedLinkText: "View price guide",
  relatedLinkTo: "/MechanicalBullPriceGuidePage"
}, {
  id: "where-to-buy",
  code: "BUY-02",
  question: "Where can I buy a mechanical bull?",
  answer: "Mechanical bulls are available from specialist equipment sellers, manufacturers, distributors and used-equipment sellers. Mechanical Bull Shop lists new and used machines with visible pricing and product information so buyers can compare available equipment before making an enquiry.",
  relatedLinkText: "Browse available machines",
  relatedLinkTo: "/ShopPage"
}, {
  id: "whats-included",
  code: "PKG-03",
  question: "What is included with a complete mechanical bull?",
  answer: "It depends on the listing. A package may include the bull, motion base, controller, inflatable safety arena, blower, cables and accessories. Always confirm the included equipment for the exact product.",
  relatedLinkText: "Read buying guide",
  relatedLinkTo: "/MechanicalBullBuyingGuidePage"
}, {
  id: "new-or-used",
  code: "COND-04",
  question: "Should I buy a new or used mechanical bull?",
  answer: "New equipment offers a known starting condition, while used equipment can reduce the initial purchase cost. For used machines, compare the condition, controller, inflatable, included equipment and available history before deciding.",
  relatedLinkText: "Read buying guide",
  relatedLinkTo: "/MechanicalBullBuyingGuidePage"
}, {
  id: "electrical-supply",
  code: "ELEC-05",
  question: "What electrical supply does a mechanical bull require?",
  answer: "Electrical requirements vary between models. Check the product-specific requirements for the bull, controller and blower before installation.",
  relatedLinkText: "View setup specifications",
  relatedLinkTo: "/SetupSafetyAndMaintenancePage"
}, {
  id: "space-needed",
  code: "SPACE-06",
  question: "How much space does a mechanical bull need?",
  answer: "Space requirements vary according to the machine and inflatable safety arena. Use the complete operating dimensions for the exact equipment rather than one universal footprint.",
  relatedLinkText: "View setup specifications",
  relatedLinkTo: "/SetupSafetyAndMaintenancePage"
}, {
  id: "replacement-parts",
  code: "PARTS-07",
  question: "Do you sell mechanical bull replacement parts?",
  answer: "Mechanical Bull Shop lists parts and accessories when available, including controllers, inflatable components, blowers and other replacement items. Confirm compatibility with the exact machine before ordering.",
  relatedLinkText: "Browse replacement parts",
  relatedLinkTo: "/MechanicalBullPartsAndAccessoriesPage"
}, {
  id: "shipping",
  code: "SHIP-08",
  question: "Can a mechanical bull be shipped to me?",
  answer: "Delivery options depend on the machine, package, destination and receiving requirements. Use the product enquiry process to provide the destination information needed to review shipping for the exact order.",
  relatedLinkText: "View shipping info",
  relatedLinkTo: "/ShippingWarrantyAndOrderingPage"
}];
export default function Faq() {
  const [openId, setOpenId] = useState<string | null>("bull-cost");
  const prefersReduced = useReducedMotion();
  const toggleAccordion = (id: string) => {
    setOpenId(prev => prev === id ? null : id);
  };
  return <section id="buyer-faq" data-nav="light" className="border-b border-border bg-background py-16 text-foreground md:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-10 lg:px-16">
        {/* Section Header */}
        <div className="border-b border-border pb-8 mb-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div className="max-w-2xl">
              <span className="font-mono text-xs font-medium tracking-wider uppercase text-primary">
                Technical Procurement
              </span>
              <h2 className="mt-2 font-serif text-3xl font-bold uppercase tracking-tight sm:text-4xl lg:text-5xl">
                Mechanical Bull Buyer FAQ
              </h2>
              <p className="mt-3 font-sans text-base text-muted-foreground sm:text-lg">
                Straight answers to common questions about mechanical bull prices, buying, equipment packages, setup, parts and delivery.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Button asChild variant="outline" className="rounded-none border-foreground font-serif uppercase tracking-wider text-foreground hover:bg-foreground hover:text-background">
                <Link to="/ContactPage">Direct Tech Support</Link>
              </Button>
              <Button asChild className="rounded-none bg-primary font-serif uppercase tracking-wider text-primary-foreground hover:bg-primary/90">
                <Link to="/MechanicalBullBuyingGuidePage">Download Buying Guide</Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Accordion List */}
        <div className="border-t border-border">
          {faqData.map((item, index) => {
          const isOpen = openId === item.id;
          return <div key={item.id} data-index={index} className="border-b border-border">
                <Button type="button" variant="ghost" onClick={() => toggleAccordion(item.id)} aria-expanded={isOpen} className="group flex h-auto w-full items-center justify-between rounded-none py-6 text-left outline-none transition-colors hover:bg-muted/40 px-2">
                  <div className="flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-6 pr-4">
                    <span className="font-mono text-xs font-medium text-muted-foreground tracking-wider uppercase shrink-0">
                      [{item.code}]
                    </span>
                    <span className="font-serif text-xl sm:text-2xl font-semibold uppercase tracking-wide text-foreground group-hover:text-primary transition-colors">
                      {item.question}
                    </span>
                  </div>

                  <div className="ml-4 flex size-8 shrink-0 items-center justify-center border border-border bg-background transition-colors group-hover:border-foreground">
                    <motion.div animate={{
                  rotate: isOpen ? 45 : 0
                }} transition={{
                  duration: prefersReduced ? 0 : 0.2,
                  ease: "easeOut"
                }}>
                      <Plus className="size-4 text-foreground" />
                    </motion.div>
                  </div>
                </Button>

                <AnimatePresence initial={false}>
                  {isOpen && <motion.div key={`content-${item.id}`} initial={prefersReduced ? {
                opacity: 1,
                height: "auto"
              } : {
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: "auto"
              }} exit={prefersReduced ? {
                opacity: 0,
                height: 0
              } : {
                opacity: 0,
                height: 0
              }} transition={{
                duration: prefersReduced ? 0 : 0.22,
                ease: [0.2, 0, 0, 1]
              }} className="overflow-hidden">
                      <div className="pb-8 pt-2 px-2 sm:pl-24">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                          <div className="lg:col-span-8">
                            <p className="font-sans text-base leading-relaxed text-muted-foreground">
                              {item.answer}
                            </p>

                            <div className="mt-4">
                              <Link to={item.relatedLinkTo} className="inline-flex items-center gap-1 font-mono text-xs uppercase tracking-wider text-primary hover:underline">
                                <span>{item.relatedLinkText}</span>
                                <ArrowUpRight className="size-3" />
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>
      </div>
    </section>;
}